import { ArrowLeftIcon } from "@phosphor-icons/react/dist/ssr";
import { defaultDateFormat } from "apps/visits/core/config";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useEffectOnce } from "react-use";
import type { ItemListId } from "@/core/modules/tracking/create-ecommerce-event";
import { useLastPageView } from "@/core/modules/tracking/use-last-page-view";
import { cn } from "@/core/shared/v2/lib/utils";
import { IntersectionFadeIn } from "@/core/shared/v2/ui/intersection-fade-in";
import { SidebarLayout } from "@/core/shared/v2/ui/sidebar-layout";
import { goToUrl } from "@/core/utils/go-to-url";
import { useCreateVoucherOrder } from "../../api/create-voucher-order";
import { type RegistrationType, useRegisterPatient } from "../../api/register-patient";
import type { CreateVoucherDTO } from "../../api/vouchers-distribution.types";
import { useFlowPath } from "../../hooks/use-flow-path";
import { usePhoneCheckoutFlow } from "../../hooks/use-phone-checkout-flow";
import { useTrackCheckoutForm } from "../../hooks/use-track-checkout-form";
import type { CartProduct } from "../../models/cart-product";
import { getConfig } from "../../models/voucher-distribution";
import { useCustomerDataState } from "../../store/use-customer-data-state";
import { useUserState } from "../../store/use-user-state";
import { useVouchersDistributionState } from "../../store/use-vouchers-distribution-state";
import {
  addPaymentInfoEvent,
  safeTrackEvent,
  signUpErrorEvent,
  signUpEvent,
  signUpNewsletterEvent,
  verificationErrorEvent,
  verificationSuccessEvent,
  viewPaymentInfoEvent,
} from "../../tracking/events";
import type { ConfigName } from "../../types/config-name";
import { extractErrorCode, isOtpErrorCode } from "../../utils/extract-error-code";
import { readGaUserType, writeGaUserType } from "../../utils/ga-user-type";
import { generateArrayWithStrings } from "../../utils/generate-array-with-strings";
import { writeVdItemListIds } from "../../utils/vd-item-list-ids";
import type { FormValues } from "../user-data-form/validation-schema";
import { SideCart } from "../v2/side-cart";
import type { CardsValidationHandle } from "./card-checkout-layout";
import { CheckoutOtpForm } from "./checkout-otp-form";
import { InfoBanner } from "./info-banner";
import { NewUserOtpOverlay } from "./new-user-otp-overlay";
import { PatientForm } from "./patient-form";
import { PhoneInputStep } from "./phone-input-step";

type Route<T> = {
  apply(args: T): string;
  href(args: T): void;
  raw(): string;
};

interface SharedCheckoutProps {
  configName: ConfigName;
  title: string;
  description: string;
  onBack: () => void;
  thankYouRoute: Route<{ uuid: string }>;
  products: CartProduct[];
  totalPrice: number;
  variant?: "friends-and-family" | "standard";
  allowSkipIdentityFields?: boolean;
  sideCartAppendedContent?: React.ReactNode;
}

const ORDER_TYPE_MAP: Record<string, string> = {
  for_self: "myself",
  for_someone: "someone",
};

export function SharedCheckout({
  configName,
  title,
  description,
  onBack,
  thankYouRoute,
  products,
  totalPrice,
  variant: variantProp,
  sideCartAppendedContent,
}: SharedCheckoutProps) {
  const history = useHistory();
  const flowPath = useFlowPath();
  const { t } = useTranslation();
  const { user } = useUserState();
  const { voucherDistribution } = useVouchersDistributionState();
  const { setCustomerData, customerData } = useCustomerDataState();
  const trackCheckoutForm = useTrackCheckoutForm();
  const { getLastPageView, setLastPageView } = useLastPageView();
  const isFree = totalPrice === 0;
  const lastOrderTypeRef = useRef<string>();
  const variant = variantProp ?? (configName === "friends_and_family" ? "friends-and-family" : "standard");
  // Keeps the CTA in a loading state while we intentionally delay the
  // redirect so GTM/GA4/ad tags can flush their HTTP requests.
  const [isPendingRedirect, setIsPendingRedirect] = useState(false);
  const [isGuestCheckout, setIsGuestCheckout] = useState(false);
  const [activeCard, setActiveCard] = useState<"patient" | "customer" | null>(null);
  // Latched on registerPatient.onSuccess. Drives lockCustomerEdit on the orderer
  // card. Cleared below when the user switches phone (currentPhone goes null
  // via changePhone), since the registration was tied to that specific number.
  const [hasRegisteredPatient, setHasRegisteredPatient] = useState(false);
  const simplifiedRegistrationAvailable = voucherDistribution?.simplifiedRegistrationAvailable ?? false;

  // Form fields are zustand-backed (useCustomerDataState) so they outlive any
  // single mount of this component — without this cleanup, navigating back to
  // the catalog and re-entering checkout would re-prefill the form (incl.
  // password / PESEL / consents) from the abandoned attempt. Auth tokens
  // (verification-token-storage in localStorage, useUserState.user) and GTM
  // session marks (sessionStorage `vd_order_for`, item-list-ids) intentionally
  // remain — they're either keyed for re-use or required by the thank-you page.
  useEffect(() => {
    return () => {
      useCustomerDataState.getState().clearCustomerData();
    };
  }, []);

  useEffectOnce(() => {
    if (products.length > 0) {
      const analyticsProducts = products.map((p) => ({
        ...p,
        productType: "blood-test-package" as const,
        quantity: 1,
      }));
      const value = totalPrice;
      const numberOfProducts = products.length;

      // Fires for free baskets too (REV-164): the user has entered the payment-info checkout step
      // regardless of basket value, and analyst-side funnel reporting needs the view event to be
      // emitted symmetrically with begin_checkout/purchase (both of which are unguarded).
      //
      // userType stays null here: the user hasn't reached registration/verification yet, so the
      // dimension is genuinely unknown at this point. readGaUserType() would surface stale state
      // from a previous mount in the same tab (e.g. completing OTP for an existing account, then
      // navigating back to the catalog and re-entering checkout) and falsely report "existing".
      // The dimension is set later by usePhoneCheckoutFlow's submitOtp ("existing") and by
      // registerPatient.onSuccess ("new_registered" / "guest") and replayed on add_payment_info
      // and purchase, so funnel join still works.
      safeTrackEvent(
        () => viewPaymentInfoEvent(analyticsProducts, { value }, { numberOfProducts, userType: null }),
        "view_payment_info",
        "shared-checkout mount",
      );
    }
  });

  const config = getConfig(voucherDistribution, configName);
  const checkoutBlockedEmailDomains = config?.checkoutBlockedEmailDomains ?? [];

  // isGuestCheckout drives the new-user OTP overlay's `method` discriminator: "guest" when the
  // user toggled "kontynuuj jako gość" (simplified/draft registration), "sign_up" otherwise (full
  // registration with password). Read fresh on each event firing inside the hook so toggling the
  // CTA mid-form flips subsequent verification events accordingly.
  const phoneFlow = usePhoneCheckoutFlow({ configName, isGuestCheckout });

  // Reset the customer-card edit lock whenever the user clears their current
  // phone (the only path that does this is changePhone, which already wipes
  // the rest of the phone-flow state). Without this, switching to a different
  // number would still hide the orderer's edit CTA based on the previous
  // phone's registration.
  useEffect(() => {
    if (!phoneFlow.currentPhone) {
      setHasRegisteredPatient(false);
    }
  }, [phoneFlow.currentPhone]);

  const { mutate: registerPatientMutate, isLoading: isRegisterLoading } = useRegisterPatient({
    config: {
      onSuccess: (data, variables) => {
        // OTP token+code was redeemed atomically with /registrations — drop the
        // localStorage copy so a future visit doesn't try to replay it.
        phoneFlow.consumeVerificationToken();
        // Lock the orderer card edit CTA: the customer record is committed
        // server-side and editing it client-side would diverge from server state.
        // The lock is keyed to the current phone — if the user later hits
        // "Zmień numer", the effect below clears it.
        setHasRegisteredPatient(true);

        // Success-side symmetry with the error path (which already fires verification_error +
        // sign_up_error together for standard-flow OTP failures):
        //   - standard registration: verification_success {method:"sign_up"} THEN sign_up.
        //     Order matters per analyst spec — the OTP-confirmation step of the funnel must be
        //     captured before the account-creation step.
        //   - simplified (guest) registration: only verification_success {method:"guest"}.
        // user_id stays null for guests per GAO REV-145; for standard/account it's the patient
        // uuid returned by the registration response (REV-167).
        const patientUuid = data.patient?.uuid ?? null;
        if (variables.registrationType === "standard") {
          // REV-170: mirror GAO — propagate the just-acquired patient uuid to lastPageView so the
          // next page_view (post-checkout redirect / TYP) carries it as user_id. Guest registration
          // intentionally skips this (user_id stays null for guests per GAO REV-145).
          // REV-211: same for user_ordered_visits (?? 0 — backend sends null for restricted_app_access).
          const userOrderedVisits = data.patient?.visitsCount ?? 0;
          const lastPageView = getLastPageView();
          if (lastPageView) {
            setLastPageView({ ...lastPageView, user_id: patientUuid, user_ordered_visits: userOrderedVisits });
          }
          safeTrackEvent(
            () => verificationSuccessEvent({ userId: patientUuid, method: "sign_up" }),
            "verification_success",
            "shared-checkout registerPatient onSuccess standard",
          );
          safeTrackEvent(
            () => signUpEvent({ userId: patientUuid }),
            "sign_up",
            "shared-checkout registerPatient onSuccess standard",
          );
          writeGaUserType("new_registered");
        } else {
          safeTrackEvent(
            () => verificationSuccessEvent({ userId: null, method: "guest" }),
            "verification_success",
            "shared-checkout registerPatient onSuccess simplified",
          );
          writeGaUserType("guest");
        }

        // After successful registration, proceed with order creation
        if (pendingOrderData.current) {
          mutate(pendingOrderData.current);
          pendingOrderData.current = null;
        }
      },
      onError: (registrationError, variables) => {
        // OTP-related failures (invalid_code, code_expired, attempts_exceeded) get
        // routed back to the OTP step so the user can retype the code instead of
        // the form silently dying. Non-OTP errors fall through to react-query's
        // default error surface (the existing translatedError banner reads from
        // useCreateVoucherOrder, which doesn't run when registration fails).
        pendingOrderData.current = null;

        const errorType = extractErrorCode(registrationError) ?? "unknown";
        const isOtpError = isOtpErrorCode(extractErrorCode(registrationError));
        // Per analyst spec: standard-flow OTP failures fire BOTH events so the funnel
        // captures "registration failed" AND the OTP-specific reason. Simplified-flow
        // failures only fire verification_error (mirrors GAO simplify-register).
        // verification_error.method tracks the new-user OTP variant: "sign_up" for full
        // registration (password filled), "guest" for simplified/draft registration.
        if (isOtpError) {
          const method = variables.registrationType === "standard" ? "sign_up" : "guest";
          safeTrackEvent(
            () => verificationErrorEvent({ errorType, method }),
            "verification_error",
            "shared-checkout registerPatient onError otp",
          );
          if (variables.registrationType === "standard") {
            safeTrackEvent(
              () => signUpErrorEvent({ errorType }),
              "sign_up_error",
              "shared-checkout registerPatient onError otp standard",
            );
          }
        } else if (variables.registrationType === "simplified") {
          safeTrackEvent(
            () => verificationErrorEvent({ errorType, method: "guest" }),
            "verification_error",
            "shared-checkout registerPatient onError simplified",
          );
        } else {
          safeTrackEvent(
            () => signUpErrorEvent({ errorType }),
            "sign_up_error",
            "shared-checkout registerPatient onError standard",
          );
        }

        phoneFlow.handleRegistrationError(registrationError);
      },
    },
  });

  const pendingOrderData = useRef<CreateVoucherDTO | null>(null);

  const {
    mutate,
    error,
    isLoading: isOrderLoading,
  } = useCreateVoucherOrder({
    config: {
      // IMPORTANT: read form data from `variables` (the argument we pass to
      // `mutate`). Earlier this read `customerData` from the enclosing scope
      // which is a stale closure the first time the form is submitted —
      // `setCustomerData` is scheduled just before `mutate` and the re-render
      // may not land before the mutation's onSuccess fires. `variables` is
      // guaranteed to contain the exact payload that was submitted.
      onError: () => {
        // The OTP overlay is kept open through registration + order creation so
        // the user sees one continuous "confirming…" state. If order creation
        // fails, close the overlay so the patient form (and its translatedError
        // banner) becomes visible.
        phoneFlow.closeNewUserOtpOverlay();
      },
      onSuccess: (data, variables) => {
        if (products.length > 0) {
          const analyticsProducts = products.map((p) => ({
            ...p,
            productType: "blood-test-package" as const,
            quantity: 1,
          }));

          const orderForValue =
            configName === "friends_and_family" && lastOrderTypeRef.current
              ? ORDER_TYPE_MAP[lastOrderTypeRef.current]
              : undefined;

          // Fires for free baskets too (REV-164 follow-up): the checkout step has been completed
          // regardless of basket value. `paymentType: "free"` mirrors the analyst-side convention
          // for purchase events on free orders, where no real provider (PayU) is involved.
          safeTrackEvent(
            () =>
              addPaymentInfoEvent(
                analyticsProducts,
                {
                  value: Number(data.order.totalGrossPrice),
                  transactionId: data.order.uuid,
                },
                {
                  paymentType: isFree ? "free" : "PayU",
                  orderFor: orderForValue,
                  numberOfProducts: products.length,
                  userType: readGaUserType(),
                },
              ),
            "add_payment_info",
            "shared-checkout onSuccess",
          );

          trackCheckoutForm(customerData);
        }

        // REV-169: fire only on a fresh opt-in. When the patient already accepted the
        // newsletter on a previous order, phoneFlow.userData carries that consent forward
        // and the form re-sends it to the API — but the user never saw the checkbox this
        // time, so it's not a new sign-up. Without this guard the event double-fires on
        // every subsequent purchase by the same patient.
        const hadNewsletterBefore = phoneFlow.userData?.acceptNewsletter === true;
        if (variables.acceptNewsletter && !hadNewsletterBefore) {
          safeTrackEvent(() => signUpNewsletterEvent(), "sign_up_newsletter", "shared-checkout onSuccess");
        }

        // Store orderFor in sessionStorage for purchase event on TYP
        if (configName === "friends_and_family" && lastOrderTypeRef.current) {
          sessionStorage.setItem("vd_order_for", ORDER_TYPE_MAP[lastOrderTypeRef.current] || "");
        }

        // Persist item_list_id per product uuid so TYP purchase event can attribute source.
        const itemListIdMap = products.reduce<Record<string, ItemListId>>((acc, p) => {
          if (p.item_list_id) acc[p.uuid] = p.item_list_id;
          return acc;
        }, {});
        writeVdItemListIds(itemListIdMap);

        if (data.order.paymentLink) {
          // Delay redirect so GTM has time to process the checkout events
          // (sign_up_newsletter, add_payment_info) and let each matching tag's
          // HTTP request be sent before the browser unloads this page. 250 ms
          // was too tight — tags using plain fetch without keepalive would
          // drop requests on unload, causing events to never reach GA4.
          //
          // 1000 ms aligns with typical GTM+GA4 + ad-network tag timings
          // (~300–800 ms each, firing in parallel). The CTA stays in a
          // loading state during this window (see `isPendingRedirect`) so
          // the user gets immediate feedback and cannot double-submit.
          setIsPendingRedirect(true);
          setTimeout(() => goToUrl(data.order.paymentLink), 1000);
        } else {
          history.push(flowPath(thankYouRoute.apply({ uuid: data.order.uuid })));
        }
      },
    },
  });

  const isCtaLoading = isOrderLoading || isRegisterLoading || isPendingRedirect;

  // Single source of truth for the phone-input UI. Owned here (not inside PhoneInputStep)
  // so the user's dial-code choice can never be wiped by a child re-mount or a stale local
  // state. After phoneFlow.submitPhone, `phoneFlow.currentPhone` is the authoritative value
  // — we mirror it back into this state in two places: on submit success (`onChange` calls
  // are user-driven so we don't need an effect for that) and on `Zmień numer` (changePhone).
  const [phoneInputDialCode, setPhoneInputDialCode] = useState(() => customerData?.dialCode || "+48");
  const [phoneInputPhone, setPhoneInputPhone] = useState(() => customerData?.phone || "");
  const lockedDialCode = phoneFlow.currentPhone?.dialCode ?? phoneInputDialCode;
  const lockedPhone = phoneFlow.currentPhone?.phone ?? phoneInputPhone;

  // Session expired — wipe phone-flow state AND the local input mirror so the user
  // restarts from a blank phone field. changePhone alone leaves phoneInputPhone
  // populated (it's our cached value, not phoneFlow state), which would otherwise
  // pre-fill the freshly-shown PhoneInputStep with the dead number.
  const handleRetryAfterSessionExpired = () => {
    phoneFlow.changePhone();
    setPhoneInputDialCode("+48");
    setPhoneInputPhone("");
  };

  const apiError = (error?.response?.data as { error?: string } | undefined)?.error;
  const apiErrorCode =
    apiError?.toLowerCase() === "BEARER.PESEL_JEST NIEPRAWIDŁOWE".toLowerCase() ? "BEARER.PESEL_INVALID" : apiError;
  const translatedError = apiErrorCode ? t(`VOUCHERS_DISTRIBUTION.ERRORS.${apiErrorCode.toUpperCase()}`) : undefined;

  const handleSubmitForm = ({ dateOfBirth, password, passwordConfirmation, ...data }: FormValues) => {
    if (voucherDistribution) {
      const {
        firstName,
        lastName,
        email,
        phone,
        dialCode,
        pesel,
        gender,
        orderType,
        patientFirstName,
        patientLastName,
        patientPhone,
        patientDialCode,
        acceptDataProcessing,
        acceptTouNPp,
        acceptNewsletter,
        acceptMarketingPhoneContact,
      } = data;

      lastOrderTypeRef.current = orderType;

      const effectivePhone = phoneFlow.currentPhone?.phone || phone.trim();
      const effectiveDialCode = phoneFlow.currentPhone?.dialCode || dialCode;

      const shouldSkipBearer = !config?.bearerRegistrationRequired && !pesel && (!dateOfBirth || !gender);

      const effectiveFirstName = phoneFlow.userData?.firstName || firstName;
      const effectiveLastName = phoneFlow.userData?.lastName || lastName;
      const effectiveEmail = phoneFlow.userData?.email || email;
      const effectiveAcceptNewsletter = acceptNewsletter ?? phoneFlow.userData?.acceptNewsletter ?? false;
      const effectiveAcceptMarketingPhoneContact =
        acceptMarketingPhoneContact ?? phoneFlow.userData?.acceptMarketingPhoneContact ?? false;

      // Only forward consent flags when granted — omitting `false` keeps the
      // backend's stored consents untouched (relevant on re-orders for users
      // who already declined or revoked these in their account).
      const consentFields = {
        ...(effectiveAcceptNewsletter && { acceptNewsletter: true }),
        ...(effectiveAcceptMarketingPhoneContact && { acceptMarketingPhoneContact: true }),
      };

      const mutationData = {
        firstName: effectiveFirstName,
        lastName: effectiveLastName,
        email: effectiveEmail,
        phone: effectivePhone,
        dialCode: effectiveDialCode,
        acceptDataProcessing,
        acceptTouNPp,
        ...consentFields,
        externalUid: user?.externalUid,
        slug: voucherDistribution.slug,
        configName,
        productUuids: products.flatMap((prod) => generateArrayWithStrings(prod.uuid, prod.quantity)),
        bearer: shouldSkipBearer
          ? null
          : {
              firstName: orderType === "for_someone" ? patientFirstName || "" : effectiveFirstName,
              lastName: orderType === "for_someone" ? patientLastName || "" : effectiveLastName,
              email: orderType === "for_self" ? effectiveEmail : "",
              phone: orderType === "for_someone" ? (patientPhone || "").trim() : effectivePhone,
              dialCode: orderType === "for_someone" ? (patientPhone ? patientDialCode || "" : "") : effectiveDialCode,
              pesel: pesel ?? undefined,
              gender: (gender as "female" | "male") ?? undefined,
              ...(dateOfBirth && {
                dateOfBirth: dayjs(dateOfBirth).format(defaultDateFormat),
              }),
            },
      } satisfies CreateVoucherDTO;

      // Persist effectiveDialCode/Phone (not raw form data) so any later read of customerData
      // is consistent with what we sent to the API. Form's `dialCode` field is hidden once
      // phoneCheckoutStep is active and may be undefined; without this the next visit would
      // fall back to "+48" instead of the user's actual choice.
      setCustomerData({ ...data, dateOfBirth, dialCode: effectiveDialCode, phone: effectivePhone });

      if (phoneFlow.isNewUserOtpVerified) {
        // OTP was verified locally (code stashed) — register the patient atomically with
        // the verification code, then on success fall through to order creation.
        // Keyed on `isNewUserOtpVerified` rather than `step === "NewUser"` because the
        // F&F overlay flow now keeps step at `NewUserOTPOverlay` through registration.
        //
        // Guest checkout maps to a "simplified" registration on the backend (draft account
        // without password/email). It runs through the same /registrations endpoint as
        // full registration; only the fields differ. `password` is empty for guest because
        // PatientForm clears it on toggle, so the original `!password` check already covers
        // both "guest" and "no password chosen" cases.
        const registrationType: RegistrationType =
          simplifiedRegistrationAvailable && !password ? "simplified" : "standard";
        pendingOrderData.current = mutationData;
        registerPatientMutate({
          slug: voucherDistribution.slug,
          configName,
          registrationType,
          dialCode: effectiveDialCode,
          phone: effectivePhone,
          firstName: effectiveFirstName,
          lastName: effectiveLastName,
          email: registrationType === "standard" ? effectiveEmail : undefined,
          password: registrationType === "standard" ? password : undefined,
          acceptDataProcessing,
          acceptTouNPp,
          ...consentFields,
          verificationToken: phoneFlow.verificationToken ?? "",
          verificationCode: phoneFlow.pendingVerificationCode ?? "",
        });
      } else {
        mutate(mutationData);
      }
    }
  };

  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const phoneAuthSectionRef = useRef<HTMLDivElement>(null);
  const validateFormRef = useRef<(() => Promise<boolean>) | null>(null);
  // F&F-only: lets us ask CardCheckoutLayout "are required cards still empty?"
  // when the page-level CTA is clicked. The check also flashes in-card banners
  // as a side effect, so we just gate on the result.
  const cardsValidationRef = useRef<CardsValidationHandle | null>(null);
  // Set when the user just completed the OTP overlay so we can submit the
  // form on the next render — after PatientForm remounts and `submitButtonRef`
  // points at a freshly mounted button. Using a ref + effect avoids the race
  // we'd hit with a synchronous setTimeout(0) against a detached button.
  const pendingPostOtpSubmitRef = useRef(false);

  useEffect(() => {
    if (phoneFlow.step !== "PhoneInput" && phoneFlow.step !== "VerifyingPhone") {
      setTimeout(() => {
        phoneAuthSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    }
  }, [phoneFlow.step]);

  useEffect(() => {
    if (pendingPostOtpSubmitRef.current && phoneFlow.isNewUserOtpVerified && submitButtonRef.current) {
      pendingPostOtpSubmitRef.current = false;
      submitButtonRef.current.click();
    }
  }, [phoneFlow.isNewUserOtpVerified]);

  return (
    <IntersectionFadeIn className={cn(["mx-auto w-full max-w-[1276px] px-4 pt-8 pb-14 md:px-8 md:pb-20 lg:px-12"])}>
      <div className={cn("mb-6 flex flex-col items-start")}>
        <div className={cn("flex flex-col items-start")}>
          <h1 className="font-serif text-2xl lg:text-4xl">{title}</h1>
          <button
            type="button"
            className={cn(
              "mt-1 flex items-center border-b text-gray-600 text-sm",
              // Mobile-only hide while the user is editing card data or filling the
              // registration form — keeps focus on the form. NewUserOTPOverlay is
              // deliberately excluded so the user retains an escape hatch during OTP.
              (activeCard !== null || phoneFlow.step === "NewUser") && "max-lg:hidden",
            )}
            onClick={onBack}
          >
            <ArrowLeftIcon />
            {t("COMMONS.GENERAL.BACK")}
          </button>
        </div>
      </div>
      <SidebarLayout>
        <div
          className={cn([
            "flex h-fit w-full flex-col gap-4 rounded-2xl bg-background p-4 shadow-sm lg:gap-3 lg:p-6 lg:py-8",
            phoneFlow.step === "NewUserOTPOverlay" ? "border-2 border-dodger-blue-700" : "border",
          ])}
        >
          {phoneFlow.step === "NewUserOTPOverlay" && (
            <NewUserOtpOverlay
              isFreeCart={isFree}
              onSubmit={(code) => {
                // The overlay stays open through registration + order creation +
                // payment redirect — see usePhoneCheckoutFlow.submitNewUserOtpOverlay.
                // PatientForm stays mounted (just hidden below) so its useForm
                // state, snapshots, and consents are preserved. The post-OTP
                // useEffect then clicks the submit button to fire registration.
                pendingPostOtpSubmitRef.current = true;
                phoneFlow.submitNewUserOtpOverlay(code);
              }}
              onResend={phoneFlow.resendOtp}
              // Includes register/order/redirect-delay so the submit & resend
              // buttons remain disabled across the full success path.
              isLoading={phoneFlow.isLoading || isCtaLoading}
              error={phoneFlow.otpError}
              resendCount={phoneFlow.otpResendCount}
              cooldownEnd={phoneFlow.otpCooldownEnd}
              resetSignal={phoneFlow.otpInputResetSignal}
              bannerMessage={
                phoneFlow.isExistingVerificationSession
                  ? t("VOUCHERS_DISTRIBUTION.PHONE_AUTH.SESSION_ALREADY_ACTIVE_NOTICE")
                  : undefined
              }
              isSessionExpired={phoneFlow.isSessionExpired}
              onRetry={handleRetryAfterSessionExpired}
            />
          )}
          <div className={cn("mx-auto w-full max-w-3xl", phoneFlow.step === "NewUserOTPOverlay" && "hidden")}>
            {translatedError && <InfoBanner message={translatedError} variant="error" className="mb-4" />}
            <PatientForm
              configName={configName}
              description={description}
              apiError={translatedError}
              apiErrorCode={apiErrorCode}
              onSubmit={handleSubmitForm}
              submitRef={submitButtonRef}
              validateRef={validateFormRef}
              bearerRegistrationRequired={!!config?.bearerRegistrationRequired}
              patientPhoneRequired={!!config?.patientPhoneRequired}
              initialValues={customerData}
              variant={variant}
              hasLabplus={config?.labplusIntegration}
              checkoutBlockedEmailDomains={checkoutBlockedEmailDomains}
              phoneCheckoutStep={phoneFlow.step}
              prefilledUserData={phoneFlow.userData}
              // Keep password fields mounted during the OTP overlay too — RHF v6
              // unregisters fields when they unmount, so toggling this back to
              // false during the overlay would wipe the password the user just
              // typed and validation would silently fail when we auto-submit
              // after OTP.
              showPasswordFields={
                !isGuestCheckout && (phoneFlow.step === "NewUser" || phoneFlow.step === "NewUserOTPOverlay")
              }
              readonlyCustomerFields={phoneFlow.step === "DataLoaded"}
              currentPhone={phoneFlow.currentPhone}
              simplifiedRegistrationAvailable={simplifiedRegistrationAvailable}
              isGuestCheckout={isGuestCheckout}
              onGuestCheckoutChange={setIsGuestCheckout}
              activeCard={activeCard}
              onActiveCardChange={setActiveCard}
              lockCustomerEdit={hasRegisteredPatient}
              cardsValidationRef={cardsValidationRef}
              phoneAuthContent={
                <div ref={phoneAuthSectionRef}>
                  <PhoneInputStep
                    dialCode={lockedDialCode}
                    phone={lockedPhone}
                    onDialCodeChange={setPhoneInputDialCode}
                    onPhoneChange={setPhoneInputPhone}
                    onSubmit={(dc, p) => {
                      // Mirror submitted values into local state so a future "Zmień numer"
                      // restores the user's last choice instead of falling back to "+48".
                      setPhoneInputDialCode(dc);
                      setPhoneInputPhone(p);
                      phoneFlow.submitPhone(dc, p);
                    }}
                    onChangeTrigger={phoneFlow.changePhone}
                    isLoading={phoneFlow.isLoading}
                    isLocked={phoneFlow.step !== "PhoneInput"}
                    error={phoneFlow.phoneError}
                  />

                  {phoneFlow.step === "VerifyingPhone" && (
                    <div className="fade-in mb-6 flex animate-in items-center justify-center fill-mode-both py-8 duration-300">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
                    </div>
                  )}

                  {phoneFlow.step === "HasAccountOTP" && (
                    <div className="fade-in slide-in-from-top-4 mt-3 animate-in fill-mode-both duration-500">
                      <CheckoutOtpForm
                        onSubmit={phoneFlow.submitOtp}
                        onResend={phoneFlow.resendOtp}
                        isLoading={phoneFlow.isLoading}
                        error={phoneFlow.otpError}
                        resendCount={phoneFlow.otpResendCount}
                        cooldownEnd={phoneFlow.otpCooldownEnd}
                        purpose="correlation"
                        bannerMessage={
                          phoneFlow.isExistingVerificationSession
                            ? t("VOUCHERS_DISTRIBUTION.PHONE_AUTH.SESSION_ALREADY_ACTIVE_NOTICE")
                            : undefined
                        }
                        resetSignal={phoneFlow.otpInputResetSignal}
                        isSessionExpired={phoneFlow.isSessionExpired}
                        onRetry={handleRetryAfterSessionExpired}
                      />
                    </div>
                  )}

                  {phoneFlow.step === "DataLoaded" && (
                    <div className="fade-in slide-in-from-top-4 mt-3 animate-in fill-mode-both duration-500">
                      <InfoBanner
                        message={t("VOUCHERS_DISTRIBUTION.PHONE_AUTH.DATA_LOADED_NOTICE")}
                        variant="info"
                        className="mb-2"
                      />
                    </div>
                  )}
                </div>
              }
            />
          </div>
        </div>
        <SideCart
          ctaText={t(
            isFree
              ? "VOUCHERS_DISTRIBUTION.FREE_PACKAGES.SUBMIT_USER_DATA_FORM"
              : "VOUCHERS_DISTRIBUTION.FREE_PACKAGES.GO_TO_PAYMENT",
          )}
          ctaSize="xl"
          showPaymentIcons={!isFree}
          onNextStep={async () => {
            if (isCtaLoading) return;
            if (activeCard === "patient") {
              const actions = document.getElementById("patient-card-actions");
              if (actions) {
                actions.scrollIntoView({ behavior: "smooth", block: "center" });
              }
              return;
            }
            if (activeCard === "customer") {
              const actions = document.getElementById("customer-card-actions");
              if (actions) {
                actions.scrollIntoView({ behavior: "smooth", block: "center" });
              }
              return;
            }
            // F&F: both cards must be saved before we attempt OTP/submit. The handle
            // sets the in-card flash banners as a side effect; we just stop here when
            // anything's still empty so the user can finish filling first. The ref is
            // unset for the standard variant (no CardCheckoutLayout mounted), so this
            // is a no-op there.
            const cardsCheck = cardsValidationRef.current?.checkAndFlash();
            if (cardsCheck && (cardsCheck.patientNeeded || cardsCheck.customerNeeded)) {
              return;
            }
            // OTP form is open and the code hasn't been confirmed yet — clicking the
            // side-cart CTA at this point would submit the registration/order form
            // unauthenticated and surface a misleading "you're not logged in" error.
            // No-op so the user advances via the OTP form's own "Potwierdź kod" button.
            if (
              phoneFlow.step === "HasAccountOTP" ||
              (phoneFlow.step === "NewUserOTPOverlay" && !phoneFlow.isNewUserOtpVerified)
            ) {
              return;
            }
            if (phoneFlow.step === "NewUser" && !phoneFlow.isNewUserOtpVerified) {
              // Validate the form before burning a phone_verifications round-trip.
              const isValid = (await validateFormRef.current?.()) ?? true;
              if (!isValid) return;
              // Backend-driven gatekeeping: re-run phone_verifications and let
              // the response decide. registration_required → OTP overlay (fresh
              // user). patient_session_already_active → skip OTP (e.g. retry
              // after a previous /registrations succeeded but /orders failed).
              // verification_required / verification_session_already_active →
              // switch to HasAccountOTP. The hook handles the step transitions
              // internally; we only get the `onSubmit` callback for the
              // skip-OTP branch where the form should fire immediately.
              phoneFlow.preflightVerifyForSubmit({
                onSubmit: () => submitButtonRef.current?.click(),
              });
            } else {
              submitButtonRef.current?.click();
            }
          }}
          ctaLoading={isCtaLoading || phoneFlow.isPreflightLoading}
          sticky={false}
          appendedContent={sideCartAppendedContent}
          isCtaDisabled={isCtaLoading || phoneFlow.isPreflightLoading}
          // Free up mobile screen real estate while the user is editing one of
          // the F&F cards or confirming the OTP overlay (which carries its own
          // submit CTA, so showing the cart's CTA underneath only adds noise).
          // Desktop keeps the cart in the sidebar column.
          isHidden={activeCard !== null || phoneFlow.step === "NewUserOTPOverlay"}
        />
      </SidebarLayout>
    </IntersectionFadeIn>
  );
}
